import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { SparkleEffect } from '../components/SparkleEffect';
import { GlitterHeart } from '../components/GlitterHeart';
import { FlameIcon, HeartIcon, SparklesIcon, ArrowRightIcon } from 'lucide-react';
export function AboutPage() {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  return <div className="min-h-screen overflow-hidden" data-id="element-153">
      {/* STORY SECTION — the main hero-like section matching the reference */}
      <section className="relative pt-28 pb-16 md:pt-36 md:pb-24 overflow-hidden" style={{
      background: 'linear-gradient(135deg, #E8A0BF 0%, #D4789C 25%, #C9709A 50%, #D4789C 75%, #E0A0C0 100%)'
    }} data-id="element-154">
        {/* Warm glow overlays for depth */}
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-white/15 rounded-full blur-[120px] -translate-x-1/3 -translate-y-1/4" data-id="element-155" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-pink-300/20 rounded-full blur-[100px] translate-x-1/4 translate-y-1/4" data-id="element-156" />
        <div className="absolute top-1/3 right-1/4 w-[300px] h-[300px] bg-white/10 rounded-full blur-[80px]" data-id="element-157" />
        <div className="absolute bottom-1/4 left-1/3 w-[250px] h-[250px] bg-pink-200/15 rounded-full blur-[60px]" data-id="element-158" />

        {/* Dense sparkles */}
        <SparkleEffect count={120} data-id="element-159" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-160">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 items-start" data-id="element-161">
            {/* Left: Text Content — 3 columns */}
            <motion.div variants={containerVariants} initial="hidden" animate="visible" className="lg:col-span-3 space-y-5 pt-4" data-id="element-162">
              <motion.div variants={itemVariants} className="flex items-center gap-3 flex-wrap mb-2" data-id="element-163">
                <h1 className="font-heading text-4xl md:text-5xl lg:text-[3.5rem] font-bold text-plum-900 italic leading-tight" data-id="element-164">
                  This is where my story changed...
                </h1>
                <GlitterHeart size={28} data-id="element-165" />
              </motion.div>

              <motion.div variants={containerVariants} initial="hidden" animate="visible" className="space-y-4 text-[15px] md:text-[17px] text-plum-900 font-medium leading-relaxed" data-id="element-166">
                <motion.p variants={itemVariants} data-id="element-167">
                  <strong data-id="element-168">
                    I've been around death since I was five years old.
                  </strong>
                </motion.p>
                <motion.p variants={itemVariants} data-id="element-169">
                  I'm a widow who lost her husband to cancer. I'm raising three
                  incredible kids—one of them with autism. And if that wasn't
                  enough, I've lived with ADHD and depression my entire life.
                </motion.p>
                <motion.p variants={itemVariants} data-id="element-170">
                  <strong data-id="element-171">Recently, I got laid off from my job.</strong>
                </motion.p>
                <motion.p variants={itemVariants} data-id="element-172">
                  And instead of letting it break me, I said—
                </motion.p>
                <motion.p variants={itemVariants} className="text-2xl md:text-3xl font-heading italic text-plum-900 my-3 pl-5 border-l-4 border-white/60" data-id="element-173">
                  "F*ck it. I'm going to help people."
                </motion.p>
                <motion.p variants={itemVariants} data-id="element-174">
                  That's what led me here.
                </motion.p>
                <motion.p variants={itemVariants} data-id="element-175">
                  I became a mindset and <strong data-id="element-176">life coach</strong> because I
                  want to be the person I <strong data-id="element-177">needed</strong> when I was
                  going through my hardest moments. I help people push through
                  whatever the hell is holding them back from actually living
                  their life.
                </motion.p>
                <motion.p variants={itemVariants} data-id="element-178">
                  I'm big on body <strong data-id="element-179">positivity</strong>,{' '}
                  <strong data-id="element-180">self-care</strong>, and <em data-id="element-181">actually</em> taking care
                  of yourself. Not the trendy version—the real, messy, everyday
                  version that keeps you going when life gets hard.
                </motion.p>
                <motion.p variants={itemVariants} data-id="element-182">
                  I focus on confidence, motivation, and helping you{' '}
                  <strong data-id="element-183">see your worth</strong>—even when you've forgotten it.
                </motion.p>
                <motion.p variants={itemVariants} data-id="element-184">
                  Look, I've got a <strong data-id="element-185">sassy mouth</strong> and I cuss a
                  lot. I <strong data-id="element-186">don't do fake</strong>. But I've got a heart of
                  gold, and all I want to do is help rebuild the world{' '}
                  <strong data-id="element-187">one soul at a time</strong>.
                </motion.p>
              </motion.div>
            </motion.div>

            {/* Right: Phone Mockup — 2 columns */}
            <motion.div initial={{
            opacity: 0,
            x: 40
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8,
            ease: 'easeOut',
            delay: 0.3
          }} className="lg:col-span-2 relative flex justify-center items-start lg:sticky lg:top-32" data-id="element-188">
              {/* Strong radial glow behind phone */}
              <div className="absolute w-[350px] h-[450px] bg-white/25 rounded-full blur-[80px] z-10 top-10" data-id="element-189" />
              <div className="absolute w-[250px] h-[250px] bg-pink-200/30 rounded-full blur-[60px] z-10 top-20 -right-4" data-id="element-190" />

              {/* Phone Mockup Image */}
              <img src="https://cdn.magicpatterns.com/uploads/7YMteDbmLXMFzxLb35vBEd/ChatGPT_Image_Mar_21,_2026,_02_52_33_PM.png" alt="Mandy - phone mockup" className="relative z-20 w-full max-w-[340px] drop-shadow-2xl" data-id="element-191" />

              {/* Floating hearts near phone */}
              <motion.div animate={{
              y: [-8, 8, -8],
              rotate: [-5, 5, -5]
            }} transition={{
              duration: 5,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="absolute top-8 -right-2 md:right-2 z-30" data-id="element-192">
                <GlitterHeart size={22} variant="light" className="opacity-80" data-id="element-193" />
              </motion.div>

              <motion.div animate={{
              y: [6, -6, 6]
            }} transition={{
              duration: 6,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 2
            }} className="absolute bottom-20 -left-4 z-30" data-id="element-194">
                <GlitterHeart size={18} className="opacity-60" data-id="element-195" />
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* QUOTE SECTION */}
      <section className="py-16 md:py-20 relative overflow-hidden bg-gradient-to-b from-blush-200 to-blush-100" data-id="element-196">
        <SparkleEffect count={40} data-id="element-197" />
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10" data-id="element-198">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-199">
            <GlitterHeart size={40} className="mx-auto mb-6" data-id="element-200" />
            <blockquote className="font-heading text-3xl md:text-5xl text-plum-800 leading-tight font-bold italic" data-id="element-201">
              "I've been through hell and I'm still standing. Now, I'm here to
              help you do the same."
            </blockquote>
          </motion.div>
        </div>
      </section>

      {/* PHILOSOPHY / VALUES SECTION */}
      <section className="py-16 md:py-20 relative overflow-hidden bg-plum-900" data-id="element-202">
        <SparkleEffect count={60} data-id="element-203" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-204">
          <div className="text-center mb-12 md:mb-16" data-id="element-205">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="font-heading text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-206">
              How We Roll
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="text-plum-200 text-lg max-w-2xl mx-auto font-medium" data-id="element-207">
              The core values that guide everything I do.
            </motion.p>
          </div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-208">
            <motion.div variants={itemVariants} className="bg-white/10 backdrop-blur-lg border border-white/20 p-8 rounded-[2rem]" data-id="element-209">
              <div className="w-14 h-14 bg-cta-500/20 rounded-2xl flex items-center justify-center mb-6" data-id="element-210">
                <FlameIcon className="w-7 h-7 text-cta-400" data-id="element-211" />
              </div>
              <h3 className="font-heading text-xl font-bold text-white mb-3" data-id="element-212">
                No Fake Shit
              </h3>
              <p className="text-plum-100 font-medium leading-relaxed text-sm" data-id="element-213">
                Showing up as our true, messy, beautiful selves. We leave the
                masks at the door.
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="bg-white/10 backdrop-blur-lg border border-white/20 p-8 rounded-[2rem]" data-id="element-214">
              <div className="w-14 h-14 bg-blush-500/20 rounded-2xl flex items-center justify-center mb-6" data-id="element-215">
                <HeartIcon className="w-7 h-7 text-blush-300" data-id="element-216" />
              </div>
              <h3 className="font-heading text-xl font-bold text-white mb-3" data-id="element-217">
                Grow Through It
              </h3>
              <p className="text-plum-100 font-medium leading-relaxed text-sm" data-id="element-218">
                Embracing the uncomfortable moments because that's where the
                real badassery is built.
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="bg-white/10 backdrop-blur-lg border border-white/20 p-8 rounded-[2rem]" data-id="element-219">
              <div className="w-14 h-14 bg-lavender-500/20 rounded-2xl flex items-center justify-center mb-6" data-id="element-220">
                <SparklesIcon className="w-7 h-7 text-lavender-300" data-id="element-221" />
              </div>
              <h3 className="font-heading text-xl font-bold text-white mb-3" data-id="element-222">
                We Rise Together
              </h3>
              <p className="text-plum-100 font-medium leading-relaxed text-sm" data-id="element-223">
                Healing is an inside job, but you don't have to do it alone.
                I've got your back.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-16 md:py-20 relative overflow-hidden bg-gradient-to-b from-blush-100 to-blush-50" data-id="element-224">
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10" data-id="element-225">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="flex flex-col items-center" data-id="element-226">
            <GlitterHeart size={56} className="mb-6" data-id="element-227" />

            <h2 className="font-heading text-4xl md:text-5xl font-bold text-plum-900 mb-10 leading-tight" data-id="element-228">
              Ready to stop just surviving?
            </h2>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 w-full" data-id="element-229">
              <Link to="/courses" className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-white text-plum-700 font-bold text-lg border-2 border-blush-300 hover:bg-blush-50 hover:border-cta-400 transition-all shadow-lg hover:-translate-y-1" data-id="element-230">
                <GlitterHeart size={22} data-id="element-231" />
                Explore Courses
              </Link>
              <Link to="/coaching" className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-cta-500 text-white font-bold text-lg hover:bg-cta-600 transition-all shadow-xl shadow-cta-500/30 hover:-translate-y-1 hover:scale-105" data-id="element-232">
                <GlitterHeart size={22} variant="light" data-id="element-233" />
                Book 1:1 Coaching
                <ArrowRightIcon className="w-5 h-5 ml-1" data-id="element-234" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
}